import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    Alert,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { createBaseOption, brandColors } from '../utils/chartOptions';

const ANALYTICS_ENDPOINT = 'https://greca.dev.br/api/analytics/euseidissodeputado.com.br';

const StatCard = ({ value, label }) => (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${brandColors.cinzaClaro}`, borderRadius: 2 }}>
        <Typography variant="h3" sx={{ color: brandColors.azul, fontWeight: 700, lineHeight: 1 }}>
            {value}
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.cinza, mt: 1 }}>
            {label}
        </Typography>
    </Paper>
);

const ChartCard = ({ title, option, height = 320 }) => (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${brandColors.cinzaClaro}`, borderRadius: 2, height: '100%' }}>
        <Typography variant="h6" sx={{ color: brandColors.azul, fontWeight: 600, mb: 1 }}>
            {title}
        </Typography>
        <ReactECharts option={option} style={{ height }} notMerge />
    </Paper>
);

const aggregateByGranularity = (byDay, granularity) => {
    if (granularity === 'day') return byDay;
    const buckets = new Map();
    byDay.forEach((d) => {
        const key = granularity === 'month' ? d.date.slice(0, 7) : d.date.slice(0, 4);
        const existing = buckets.get(key) || { date: key, pageviews: 0, visits: 0 };
        existing.pageviews += d.pageviews;
        existing.visits += d.visits;
        buckets.set(key, existing);
    });
    return Array.from(buckets.values());
};

const barOption = (title, items) => {
    const sorted = [...items].sort((a, b) => a.count - b.count);
    return {
        ...createBaseOption(title),
        grid: { left: 110, right: 24, top: 10, bottom: 10 },
        xAxis: { type: 'value' },
        yAxis: { type: 'category', data: sorted.map((i) => i.label) },
        series: [{
            type: 'bar',
            data: sorted.map((i) => i.count),
            itemStyle: { color: brandColors.verde },
            barMaxWidth: 22,
        }],
    };
};

const pieOption = (title, items) => ({
    ...createBaseOption(title),
    series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        data: items.map((i) => ({ name: i.label, value: i.count })),
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        color: [brandColors.verde, brandColors.azul, brandColors.amarelo, brandColors.laranjaClaro, brandColors.azulClaro],
    }],
});

const AdminPipeline = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [granularity, setGranularity] = useState('day');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${ANALYTICS_ENDPOINT}?days=90`);
            if (!res.ok) throw new Error(`status ${res.status}`);
            const json = await res.json();
            setData(json);
        } catch (e) {
            setError('Não consegui carregar as métricas agora.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const timelineOption = useMemo(() => {
        if (!data) return {};
        const rows = aggregateByGranularity(data.by_day, granularity);
        return {
            ...createBaseOption('Visitas ao longo do tempo'),
            tooltip: { trigger: 'axis' },
            legend: { data: ['Page views', 'Visitas'], top: '10%' },
            grid: { left: 50, right: 24, top: 60, bottom: 30 },
            xAxis: { type: 'category', data: rows.map((r) => r.date) },
            yAxis: { type: 'value' },
            series: [
                { name: 'Page views', type: 'line', smooth: true, data: rows.map((r) => r.pageviews), color: brandColors.verde },
                { name: 'Visitas', type: 'line', smooth: true, data: rows.map((r) => r.visits), color: brandColors.azul },
            ],
        };
    }, [data, granularity]);

    if (loading) {
        return (
            <Container sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ py: 8 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <Typography variant="h4" sx={{ color: brandColors.azul, fontWeight: 700, mb: 1 }}>
                Métricas do site
            </Typography>
            <Typography variant="body1" sx={{ color: brandColors.cinza, mb: 3 }}>
                Visitas, páginas, países e dispositivos, direto da Cloudflare.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <StatCard value={data.totals.pageviews} label={`Page views (${data.days} dias)`} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard value={data.totals.visits} label={`Visitas (${data.days} dias)`} />
                </Grid>
            </Grid>

            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={granularity}
                    onChange={(e, val) => val && setGranularity(val)}
                >
                    <ToggleButton value="day">Dia</ToggleButton>
                    <ToggleButton value="month">Mês</ToggleButton>
                    <ToggleButton value="year">Ano</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <ChartCard title="" option={timelineOption} height={360} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChartCard
                        title="Páginas mais vistas"
                        option={barOption('', data.top_pages.map((p) => ({ label: p.path, count: p.pageviews })))}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChartCard title="Países" option={barOption('', data.by_country)} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChartCard title="Dispositivo" option={pieOption('', data.by_device)} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChartCard title="Navegador" option={barOption('', data.by_browser)} />
                </Grid>
                <Grid item xs={12}>
                    <ChartCard title="Sistema operacional" option={barOption('', data.by_os)} />
                </Grid>
            </Grid>
        </Container>
    );
};

export default AdminPipeline;
